/**
 * ShareAndEarn — public engagement contest. Visitor enters email → gets
 * a unique referral code + share URL → invites 3+ landlord friends →
 * unlocks the prize. Live leaderboard surfaces top referrers (masked
 * emails). This is the path c) "Engagement contests — referral codes"
 * from the GTM doc.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Sparkles, Trophy, Linkedin, Facebook, Twitter, Mail, Copy, Check, Users, ArrowRight, Loader2,
} from "lucide-react";
import PublicHeader from "@/components/layout/PublicHeader";
import SmartChatBubble from "@/components/SmartChatBubble";
import { useBotShield, BotHoneypot } from "@/lib/botShield";

const API = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
const SITE = "https://centralrentals.ca";

function setMeta(sel, attrs) {
  let el = document.head.querySelector(sel);
  if (!el) { el = document.createElement(sel.startsWith("link") ? "link" : "meta"); document.head.appendChild(el); }
  Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v));
  return el;
}

function ShareRow({ url, message, code, onTouch }) {
  const enc = encodeURIComponent;
  const li = `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}`;
  const fb = `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}`;
  const tw = `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(message)}`;
  const mail = `mailto:?subject=${enc("Free tool for Canadian landlords")}&body=${enc(message + "\n\n" + url)}`;
  const [copied, setCopied] = useState(false);
  function copy() {
    try {
      navigator.clipboard.writeText(url).catch(() => {
        // Sandboxed iframe (preview): silently fall back to a temporary
        // textarea + execCommand — works in most browsers without
        // triggering the Permissions-Policy error overlay.
        try {
          const ta = document.createElement("textarea");
          ta.value = url; ta.style.position = "fixed"; ta.style.left = "-9999px";
          document.body.appendChild(ta); ta.select();
          document.execCommand("copy");
          document.body.removeChild(ta);
        } catch { /* silent */ }
      });
    } catch { /* silent — older browsers without navigator.clipboard */ }
    setCopied(true); setTimeout(() => setCopied(false), 1500);
    onTouch?.("copy");
  }
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="share-row">
      <a href={li} target="_blank" rel="noopener noreferrer" onClick={() => onTouch?.("linkedin")} data-testid="share-linkedin" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-700 text-white text-xs font-bold hover:bg-blue-800"><Linkedin className="w-3.5 h-3.5"/> LinkedIn</a>
      <a href={fb} target="_blank" rel="noopener noreferrer" onClick={() => onTouch?.("facebook")} data-testid="share-facebook" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-600 text-white text-xs font-bold hover:bg-blue-700"><Facebook className="w-3.5 h-3.5"/> Facebook</a>
      <a href={tw} target="_blank" rel="noopener noreferrer" onClick={() => onTouch?.("twitter")} data-testid="share-twitter" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-900 text-white text-xs font-bold hover:bg-slate-800"><Twitter className="w-3.5 h-3.5"/> X</a>
      <a href={mail} onClick={() => onTouch?.("email")} data-testid="share-email" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-800 text-xs font-bold hover:bg-slate-200"><Mail className="w-3.5 h-3.5"/> Email</a>
      <button onClick={copy} data-testid="share-copy" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold">
        {copied ? <><Check className="w-3.5 h-3.5"/> Copied</> : <><Copy className="w-3.5 h-3.5"/> Copy link</>}
      </button>
      <code className="text-[11px] text-slate-500 ml-auto" data-testid="share-code-display">code: <b className="text-slate-900">{code}</b></code>
    </div>
  );
}

export default function ShareAndEarn() {
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [board, setBoard] = useState([]);
  const shield = useBotShield();

  useEffect(() => {
    document.title = "Refer 3 friends, win the prize — Central Rentals";
    const tags = [
      setMeta('meta[name="description"]', { name: "description", content: "Tag 3 Canadian landlord friends to unlock the Central Rentals referral prize. Live leaderboard." }),
      setMeta('link[rel="canonical"]', { rel: "canonical", href: `${SITE}/share-and-earn` }),
    ];
    return () => tags.forEach((t) => t?.remove?.());
  }, []);

  useEffect(() => {
    fetch(`${API}/api/growth/referrals/leaderboard`).then((r) => r.json()).then((d) => setBoard(d.leaderboard || [])).catch(() => {});
  }, [data]);

  async function getCode(e) {
    e?.preventDefault?.();
    if (!email.trim()) return;
    setBusy(true); setError("");
    try {
      const r = await fetch(`${API}/api/growth/referrals/create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), name: name.trim() || null, role: "landlord", ...shield.values() }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || "Create failed");
      }
      setData(await r.json());
      try {
        const { fireLeadFormSubmit } = await import("@/lib/google-tag");
        fireLeadFormSubmit({ source: "share_and_earn", email: email.trim(), valueCad: 5 });
      } catch { /* never block UX */ }
    } catch (err) { setError(err.message); } finally { setBusy(false); }
  }

  async function onTouch(channel) {
    if (!data?.code) return;
    try {
      await fetch(`${API}/api/growth/referrals/touch`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: data.code, friend_email: `${channel}@share.local`, kind: "tag" }),
      });
      const r = await fetch(`${API}/api/growth/referrals/${data.code}`);
      if (r.ok) setData(await r.json());
    } catch { /* noop */ }
  }

  const progress = data ? Math.min(100, Math.round((data.touches / (data.prize_target_touches || 3)) * 100)) : 0;
  const message = data
    ? `Hey — found a free tool that's actually built for Canadian landlords. Saves me hours/month on rent + tax. Use my code ${data.code} when you sign up.`
    : "";

  return (
    <div className="min-h-screen bg-white" data-testid="share-and-earn-page">
      <PublicHeader light/>

      <section className="max-w-6xl mx-auto px-5 pt-12 pb-8">
        <div className="inline-flex items-center gap-2 mb-5 px-3 py-1.5 rounded-full bg-amber-50 border border-amber-200 text-[11px] font-bold tracking-wide text-amber-800">
          <Trophy className="w-3.5 h-3.5"/> Tag 3 landlord friends, win the prize
        </div>
        <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-tighter text-slate-900">
          Refer 3 friends. Unlock the prize.
        </h1>
        <p className="mt-5 text-lg text-slate-600 max-w-2xl">
          Drop your email below to get a unique referral code. Share it
          with 3+ Canadian landlord friends — when they sign up, you
          unlock a free annual Growth plan (CA$396 value) AND get a spot
          on our public leaderboard.
        </p>

        {!data ? (
          <form onSubmit={getCode} className="mt-8 max-w-lg space-y-3" data-testid="share-form">
            <BotHoneypot {...shield.fieldProps} />
            <input data-testid="share-name" placeholder="Your first name (optional)" value={name} onChange={(e) => setName(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
            <input data-testid="share-email" type="email" placeholder="you@email.ca" value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-slate-300 rounded-lg outline-none focus:border-emerald-500"/>
            {error && <div className="text-xs text-rose-600">{error}</div>}
            <button type="submit" disabled={busy || !email.trim()} data-testid="share-submit" className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold text-sm disabled:opacity-50">
              {busy ? <><Loader2 className="w-4 h-4 animate-spin"/> Generating…</> : <><Sparkles className="w-4 h-4"/> Get my referral code</>}
            </button>
          </form>
        ) : (
          <div className="mt-8 rounded-2xl border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-white p-6 max-w-3xl" data-testid="share-card">
            <div className="font-display text-xl font-extrabold text-slate-900">Your code is ready</div>
            <p className="text-sm text-slate-600 mt-1">Share this link with your landlord network. Every click + signup brings you closer to the prize.</p>
            <div className="mt-4 p-3 bg-white border border-slate-200 rounded-lg font-mono text-xs text-slate-800 break-all" data-testid="share-url">{data.share_url}</div>
            <div className="mt-3"><ShareRow url={data.share_url} message={message} code={data.code} onTouch={onTouch}/></div>
            <div className="mt-5">
              <div className="flex items-center justify-between text-xs font-bold text-slate-600 mb-1.5">
                <span>Progress to prize</span>
                <span data-testid="share-progress">{data.touches} / {data.prize_target_touches}</span>
              </div>
              <div className="w-full h-3 rounded-full bg-slate-100 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-emerald-500 to-emerald-600 transition-all duration-500" style={{ width: `${progress}%` }}/>
              </div>
              {data.prize_eligible && <div className="mt-3 text-sm font-bold text-emerald-700 flex items-center gap-1.5" data-testid="share-prize-eligible"><Trophy className="w-4 h-4"/> You&apos;ve unlocked the prize! We&apos;ll email you within 24h.</div>}
            </div>
          </div>
        )}
      </section>

      <section className="max-w-6xl mx-auto px-5 pb-16" data-testid="share-leaderboard-section">
        <div className="text-[11px] uppercase tracking-widest font-bold text-slate-500 mb-3">Live leaderboard · Last 30 days</div>
        <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {board.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm">
              <Users className="w-8 h-8 mx-auto mb-2 text-slate-300"/>
              No referrals yet — be the first.
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[10px] uppercase tracking-widest font-bold text-slate-500">
                <tr><th className="px-4 py-2 text-left w-16">#</th><th className="px-4 py-2 text-left">Email</th><th className="px-4 py-2 text-right">Touches</th><th className="px-4 py-2 text-right">Signups</th></tr>
              </thead>
              <tbody>
                {board.map((row, i) => (
                  <tr key={row.email_masked + i} className="border-t border-slate-100" data-testid={`leaderboard-row-${i}`}>
                    <td className="px-4 py-2 font-bold tabular-nums">{i + 1}</td>
                    <td className="px-4 py-2 font-mono text-xs">{row.email_masked}</td>
                    <td className="px-4 py-2 text-right font-bold tabular-nums">{row.touches}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-emerald-700 font-bold">{row.signups}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
        <div className="mt-4 text-center">
          <Link to="/tools" data-testid="share-tools-link" className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 hover:text-emerald-800">
            Or use the free tools yourself <ArrowRight className="w-3 h-3"/>
          </Link>
        </div>
      </section>

      <SmartChatBubble/>
    </div>
  );
}
